import React, { useContext, useMemo } from "react";
import { useFortress } from "@foundation/fortress";
import { classes } from "@bloomberg/media-ui-core";
import styles from "./quotePageLayout.module.css";
import { QuoteContext } from "~context/QuoteContext";
import { getFortressState } from "~utils/getFortressState";
import Share from "~components/Share";
import QuotePagePanel from "~components/QuotePagePanel";
import SummaryContainer from "~components/SummaryContainer";
import RelatedNewsContainer from "~components/RelatedNewsContainer";
import BazaarInBodyAd from "~components/ads/BazaarInBodyAd";
import CompanyInfoContainer from "~components/CompanyInfoContainer";
import FinancialsContainer from "~components/FinancialsContainer";
import { useLocale } from "~hooks/i18n/useLocale";
import { useOnDesktop } from "~hooks/useScreenBreakpoints";
import { usePageData } from "~context/PageContext";
import type { QuotePageData } from "~types/types";
import QuoteMembers from "~components/QuoteMembers";

function MidSectionMobileAndTablet({
  isMembersPage,
  isAllowed,
  isLoading,
  isJapanese,
}: {
  isMembersPage: boolean;
  isAllowed: boolean;
  isLoading: boolean;
  isJapanese: boolean;
}) {
  if (isMembersPage) {
    return (
      <div className={styles.mobileAndTablet}>
        <QuoteMembers />

        {isAllowed && (
          <>
            <BazaarInBodyAd className={styles.mobile} />
            <CompanyInfoContainer />
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.mobileAndTablet}>
      <SummaryContainer />

      {isAllowed && (
        <>
          {!isJapanese && <RelatedNewsContainer />}
          <BazaarInBodyAd className={styles.mobile} />
          <CompanyInfoContainer />
          {!isJapanese && <FinancialsContainer />}
        </>
      )}
    </div>
  );
}

export default function MidSection() {
  const quoteData = useContext(QuoteContext);
  const fortressContext = useFortress();

  const { isAllowed, isLoading } = getFortressState(fortressContext);
  const { isJapanese } = useLocale();
  const isDesktop = useOnDesktop();
  const { pageData } = usePageData();

  const isMembersPage = useMemo(
    () => (pageData as QuotePageData)?.quotePageTab === "members",
    [pageData]
  );

  if (!quoteData) {
    return null;
  }

  const { mediaSecurityType, type, indexDescription, companyDescription } =
    quoteData;

  return (
    <div className="flex">
      <div className={styles.shareSmallDesktopContainer}>
        <Share vertical fixed className={styles.shareSmallDesktop} />
      </div>

      {/* Render only in desktop */}
      {isDesktop && (
        <QuotePagePanel
          mediaSecurityType={mediaSecurityType}
          type={type}
          indexDescription={indexDescription}
          companyDescription={companyDescription}
        />
      )}

      {/* Render only in tablet & mobile */}
      {!isDesktop && (
        <MidSectionMobileAndTablet
          isAllowed={isAllowed}
          isJapanese={isJapanese}
          isLoading={isLoading}
          isMembersPage={isMembersPage}
        />
      )}
    </div>
  );
}
